import firebase from 'firebase/app';
import 'firebase/auth';

import { getPayerBaseUrl } from '../utils/URLBuilder';

/**
 * The tagId to address an unbind with, for a binding row.
 *
 * Bindings written by the join API carry the tagId that was scanned; ones
 * repaired by hand may not. Any binding for the same template in the same team
 * resolves to the same (team, template) pair server-side, so a sibling's tagId
 * is an equally correct address — the same fallback the admin create form uses.
 *
 * @param {Object} binding the binding to release (may be null/undefined)
 * @param {Object[]} [allBindings] the team's bindings, for the sibling fallback
 * @returns {string|null}
 */
export function tagIdForBinding(binding, allBindings = []) {
  if (!binding) return null;
  if (binding.tagId) return binding.tagId;
  const sibling = (allBindings || []).find(
    (b) => b.templateTagId === binding.templateTagId && b.tagId,
  );
  return sibling ? sibling.tagId : null;
}

/**
 * Release one xmid from the member it is bound to.
 *
 * Goes through the strike-payer API rather than deleting the Firestore doc
 * directly, because the API is what emits member_removed to the partner and
 * clears the member-doc externalIds mirror. A direct delete would leave the
 * partner believing the ID is still in use, and a stale mirror key would silently
 * no-op a later re-join.
 *
 * Addressed by TAG, as the join is: DELETE /api/tags/{tagId}/bindings/{xmid}.
 * The server resolves the team and template from the tag, so no internal id
 * travels — the reference points are the same ones a partner holds. Use
 * tagIdForBinding() to pick the tagId off the binding row.
 *
 * There is deliberately no matching bind call: an xmid only ever arrives on a
 * partner's integration link, so binding is self-service and self-validating.
 * Recovery from a bad unbind is to resend that link.
 *
 * The same endpoint serves partners, who authenticate with an API key instead.
 * Here the caller is a person, so it is their Firebase ID token, and the server
 * allows it for a team manager or a Strike admin.
 *
 * @param {string} tagId the bound tag's doc id ({templateTagId}---{tagUid})
 * @param {string} xmid the partner's member id to release
 * @param {string} [clientId] which client the caller is acting as. Verified
 *   server-side against clients/{id}.ownerId, so sending it grants nothing — it
 *   removes a guess for uids that own more than one client.
 * @returns {Promise<{ok: true} | {ok: false, error: string}>}
 */
export async function unbindMemberId({ tagId, xmid, clientId }) {
  if (!tagId || !xmid) {
    return { ok: false, error: 'missing-parameters' };
  }

  const user = firebase.auth().currentUser;
  if (!user) {
    return { ok: false, error: 'not-signed-in' };
  }

  try {
    const idToken = await user.getIdToken();
    const base = getPayerBaseUrl();
    // xmid is its own segment: it is partner-supplied and may itself contain the
    // '---' that joins templateTagId and xmid into the binding doc id.
    const acting = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
    const url = `${base}/api/tags/${encodeURIComponent(tagId)}`
      + `/bindings/${encodeURIComponent(xmid)}${acting}`;

    const res = await fetch(url, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${idToken}` },
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      return { ok: false, error: body.error || `unbind-failed-${res.status}` };
    }
    // No local state to update: the caller is subscribed to memberIds, so the row
    // disappears when Firestore says it has.
    return { ok: true };
  } catch (err) {
    console.warn('unbindMemberId: request failed:', err);
    return { ok: false, error: 'network-error' };
  }
}

/** Human-readable reasons, for the ones a manager can actually act on. */
export const unbindErrorMessages = {
  'not-signed-in': 'You need to be signed in to release an ID.',
  'binding-not-found': 'That ID is no longer linked — it may already have been released.',
  'not-permitted': 'You do not have permission to release this ID.',
  'tag-not-found': 'The tag this ID was linked through can no longer be found.',
  'missing-parameters': 'This binding has no tag reference, so it cannot be released from here.',
  'network-error': 'Could not reach the server. Check your connection and try again.',
};
